import React, { Component } from "react";
import "./GateDetection.css";

/*
 * ============================================================
 * DEMO MODE
 * ============================================================
 *
 * true  -> Show dummy data
 * false -> Use real API
 *
 * Change this to false when backend API is ready.
 */
const DEMO_MODE = true;


class GateDetection extends Component {
  state = {
    loading: false,
    error: null,
    lastUpdated: new Date(),

    // ========================================================
    // Gate
    // ========================================================

    gateStatus: "OPEN",
    confidence: 0.98,

    openedAt: new Date(
      Date.now() - 18 * 1000
    ).toISOString(),

    currentOpenDuration: 18,

    // ========================================================
    // Number Plate
    // ========================================================

    lastPlate: "MH 12 AB 4582",

    plateConfidence: 0.96,

    plateDetectedAt: new Date(
      Date.now() - 8 * 1000
    ).toISOString(),

    // ========================================================
    // Recent Events
    // ========================================================

    recentEvents: [
      {
        id: "dummy-1",

        time: new Date(
          Date.now() - 8 * 1000
        ).toISOString(),

        event: "Gate Open",

        details: "CLOSED → OPEN",

        vehicleNumber: "MH 12 AB 4582",

        confidence: 0.96,

        status: "NORMAL",
      },

      {
        id: "dummy-2",

        time: new Date(
          Date.now() - 12 * 1000
        ).toISOString(),

        event: "Gate Close",

        details: "OPEN → CLOSED",

        vehicleNumber: "MH 14 CQ 7319",

        confidence: 0.94,

        status: "NORMAL",
      },

      {
        id: "dummy-3",

        time: new Date(
          Date.now() - 45 * 1000
        ).toISOString(),

        event: "Gate Open",

        details: "CLOSED → OPEN",

        vehicleNumber: "KA 05 MN 7214",

        confidence: 0.97,

        status: "NORMAL",
      },

      {
        id: "dummy-4",

        time: new Date(
          Date.now() - 75 * 1000
        ).toISOString(),

        event: "Gate Close",

        details: "OPEN → CLOSED",

        vehicleNumber: "MH 12 RT 8842",

        confidence: 0.93,

        status: "NORMAL",
      },

      {
        id: "dummy-5",

        time: new Date(
          Date.now() - 110 * 1000
        ).toISOString(),

        event: "Gate Open",

        details: "CLOSED → OPEN",

        vehicleNumber: "MH 20 BX 4521",

        confidence: 0.95,

        status: "NORMAL",
      },

      {
        id: "dummy-6",

        time: new Date(
          Date.now() - 145 * 1000
        ).toISOString(),

        event: "Gate Close",

        details: "OPEN → CLOSED",

        vehicleNumber: "MH 24 AZ 1098",

        confidence: 0.91,

        status: "NORMAL",
      },
    ],
  };


  /*
   * ============================================================
   * LIFECYCLE
   * ============================================================
   */

  componentDidMount() {
    if (DEMO_MODE) {
      this.startDemoMode();
    } else {
      this.loadGateData();
    }

    this.durationTimer = setInterval(
      this.updateOpenDuration,
      1000
    );
  }


  componentDidUpdate(prevProps) {
    if (DEMO_MODE) {
      return;
    }

    if (
      prevProps.gateId !== this.props.gateId ||
      prevProps.cameraId !== this.props.cameraId ||
      prevProps.siteId !== this.props.siteId ||
      prevProps.baseUrl !== this.props.baseUrl
    ) {
      this.loadGateData();
    }
  }


  componentWillUnmount() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }

    if (this.durationTimer) {
      clearInterval(this.durationTimer);
    }
  }


  /*
   * ============================================================
   * DEMO MODE
   * ============================================================
   */

  startDemoMode = () => {
    /*
     * Refresh dummy data every 5 seconds.
     *
     * This makes the dashboard look like a live dashboard
     * without changing the actual dummy values.
     */

    this.demoTimer = setInterval(
      () => {
        this.setState({
          lastUpdated: new Date(),
        });
      },
      5000
    );
  };


  /*
   * ============================================================
   * REAL API
   * ============================================================
   */

  apiGet = async (path) => {
    if (this.props.serverRequests?.get) {
      return this.props.serverRequests.get(path);
    }

    const response = await fetch(
      `${this.props.baseUrl || ""}${path}`,
      {
        credentials: "include",

        headers: {
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status}`
      );
    }

    return response.json();
  };


  loadGateData = async () => {
    if (DEMO_MODE) {
      return;
    }

    if (this.loadingRequest) {
      return;
    }

    this.loadingRequest = true;

    const query = new URLSearchParams();

    query.set(
      "gate_id",
      this.props.gateId || "main_gate"
    );

    if (this.props.cameraId) {
      query.set(
        "camera_id",
        this.props.cameraId
      );
    }

    if (this.props.siteId) {
      query.set(
        "site_id",
        this.props.siteId
      );
    }

    try {
      const [
        summaryResponse,
        eventsResponse,
      ] = await Promise.allSettled([
        this.apiGet(
          `/api/v1/widgets/gate-summary?${query.toString()}`
        ),

        this.apiGet(
          `/api/v1/events?event_type=gate.state_changed&${query.toString()}&limit=10`
        ),
      ]);


      /*
       * --------------------------------------------------------
       * SUMMARY
       * --------------------------------------------------------
       */

      let summary = {};
      let summaryError = null;

      if (
        summaryResponse.status ===
        "fulfilled"
      ) {
        summary =
          summaryResponse.value?.data ||
          summaryResponse.value ||
          {};
      } else {
        summaryError =
          summaryResponse.reason?.message ||
          "Gate summary unavailable";
      }


      /*
       * --------------------------------------------------------
       * EVENTS
       * --------------------------------------------------------
       */

      let events = [];

      if (
        eventsResponse.status ===
        "fulfilled"
      ) {
        const response =
          eventsResponse.value;

        events = Array.isArray(response)
          ? response
          : response?.results ||
            response?.data ||
            [];
      }


      const normalizedEvents =
        events
          .map(this.normalizeEvent)
          .filter(Boolean)
          .sort(
            (a, b) =>
              new Date(
                b.time || 0
              ).getTime() -
              new Date(
                a.time || 0
              ).getTime()
          );


      /*
       * --------------------------------------------------------
       * LATEST PLATE
       * --------------------------------------------------------
       */

      const latestPlateEvent =
        normalizedEvents.find(
          (event) =>
            event.vehicleNumber &&
            event.vehicleNumber !== "—"
        );


      /*
       * --------------------------------------------------------
       * GATE STATUS
       * --------------------------------------------------------
       */

      const gateStatus =
        String(
          summary.gate_status ||
            summary.current_state ||
            summary.state ||
            "UNKNOWN"
        ).toUpperCase();


      const confidence =
        summary.confidence ??
        summary.current_confidence ??
        null;


      const openedAt =
        summary.opened_at ||
        summary.current_session?.opened_at ||
        null;


      const currentOpenDuration =
        Number(
          summary.current_open_duration_sec ??
            summary.current_session
              ?.duration_sec ??
            0
        ) || 0;


      this.setState({
        loading: false,

        error: summaryError,

        lastUpdated: new Date(),

        gateStatus,

        confidence,

        openedAt,

        currentOpenDuration,

        lastPlate:
          latestPlateEvent?.vehicleNumber ||
          "—",

        plateConfidence:
          latestPlateEvent?.confidence ??
          null,

        plateDetectedAt:
          latestPlateEvent?.time ||
          null,

        recentEvents:
          normalizedEvents,
      });
    } catch (error) {
      console.error(
        "Gate Detection API error:",
        error
      );

      this.setState({
        loading: false,

        error:
          error.message ||
          "Unable to load Gate Detection data",
      });
    } finally {
      this.loadingRequest = false;
    }
  };


  /*
   * ============================================================
   * NORMALIZE EVENT
   * ============================================================
   */

  normalizeEvent = (event) => {
    if (!event) {
      return null;
    }

    const payload =
      event.payload || {};

    const newState =
      String(
        payload.new_state ||
          event.new_state ||
          ""
      ).toUpperCase();

    const previousState =
      String(
        payload.previous_state ||
          event.previous_state ||
          ""
      ).toUpperCase();

    const eventTime =
      event.event_time ||
      event.timestamp ||
      event.created_at ||
      null;

    const vehicleNumber =
      payload.vehicle_number ||
      payload.vehicleNumber ||
      event.vehicle_number ||
      event.vehicleNumber ||
      "—";


    let eventName =
      "Gate State Changed";

    if (newState === "OPEN") {
      eventName = "Gate Open";
    } else if (
      newState === "CLOSED"
    ) {
      eventName = "Gate Close";
    }


    return {
      id:
        event.event_id ||
        event.id ||
        `${eventTime}-${newState}`,

      time: eventTime,

      event: eventName,

      details:
        previousState && newState
          ? `${previousState} → ${newState}`
          : newState ||
            "Gate state changed",

      vehicleNumber,

      confidence:
        payload.confidence ??
        event.confidence ??
        null,

      status:
        event.status ||
        "NORMAL",
    };
  };


  /*
   * ============================================================
   * UPDATE OPEN DURATION
   * ============================================================
   */

  updateOpenDuration = () => {
    if (
      this.state.gateStatus !==
        "OPEN" ||
      !this.state.openedAt
    ) {
      return;
    }


    const openedTime =
      new Date(
        this.state.openedAt
      ).getTime();


    if (
      Number.isNaN(openedTime)
    ) {
      return;
    }


    const elapsedSeconds =
      Math.max(
        0,
        Math.floor(
          (Date.now() -
            openedTime) /
            1000
        )
      );


    this.setState({
      currentOpenDuration:
        elapsedSeconds,
    });
  };


  /*
   * ============================================================
   * FORMAT DURATION
   * ============================================================
   */

  formatDuration = (seconds) => {
    const total = Math.max(
      0,
      Math.round(
        Number(seconds) || 0
      )
    );


    const hours =
      Math.floor(
        total / 3600
      );


    const minutes =
      Math.floor(
        (total % 3600) / 60
      );


    const secs =
      total % 60;


    if (hours) {
      return `${hours}h ${minutes}m`;
    }


    if (minutes) {
      return `${minutes}m ${secs}s`;
    }


    return `${secs}s`;
  };


  /*
   * ============================================================
   * FORMAT TIME
   * ============================================================
   */

  formatTime = (value) => {
    if (!value) {
      return "—";
    }


    const date =
      new Date(value);


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return String(value);
    }


    return date.toLocaleTimeString(
      "en-IN",
      {
        hour: "2-digit",

        minute: "2-digit",

        second: "2-digit",

        hour12: true,
      }
    );
  };


  /*
   * ============================================================
   * FORMAT CONFIDENCE
   * ============================================================
   */

  formatConfidence = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "—";
    }


    const numericValue =
      Number(value);


    if (
      Number.isNaN(
        numericValue
      )
    ) {
      return "—";
    }


    const percentage =
      numericValue <= 1
        ? numericValue * 100
        : numericValue;


    return `${Math.round(
      percentage
    )}%`;
  };


  /*
   * ============================================================
   * VIEW ALL EVENTS
   * ============================================================
   */

  viewAllEvents = () => {
    if (
      typeof this.props
        .onViewEvents ===
      "function"
    ) {
      this.props.onViewEvents({
        gateId:
          this.props.gateId,

        cameraId:
          this.props.cameraId,

        siteId:
          this.props.siteId,
      });
    }
  };


  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  render() {
    const {
      loading,
      error,
      lastUpdated,

      gateStatus,
      confidence,
      openedAt,
      currentOpenDuration,

      lastPlate,
      plateConfidence,
      plateDetectedAt,

      recentEvents,
    } = this.state;


    const isOpen =
      gateStatus === "OPEN";

    const isClosed =
      gateStatus === "CLOSED";

    const isUnknown =
      gateStatus === "UNKNOWN";


    const gateName =
      this.props.gateName ||
      "Main Entrance";


    const siteName =
      this.props.siteName ||
      "Kirti Mill";


    const gateStatusClass =
      isOpen
        ? "open-text"
        : isUnknown
          ? "unknown-text"
          : "closed-text";


    const gateBadgeClass =
      isOpen
        ? "badge-open"
        : isUnknown
          ? "badge-unknown"
          : "badge-closed";


    return (
      <div className="gate-widget">

        {/* =====================================================
            DEMO MODE INDICATOR
        ====================================================== */}

        {DEMO_MODE && (
          <div className="demo-mode-banner">
            <span className="demo-dot" />

            <span>
              {/* DEMO MODE — Showing dummy Gate Detection data */}
            </span>

            <span className="demo-hint">
              {/* Set DEMO_MODE = false to use API */}
            </span>
          </div>
        )}


        {/* =====================================================
            ERROR
        ====================================================== */}

        {error && (
          <div className="gate-error-banner">

            <strong>
              Gate data unavailable.
            </strong>

            <span>
              {error}
            </span>

            <button
              onClick={
                this.loadGateData
              }
            >
              Retry
            </button>

          </div>
        )}


        {/* =====================================================
            KPI CARDS
        ====================================================== */}

        <div className="gate-kpi-grid">

          {/* Gate Status */}

          <div className="gate-kpi-card">

            <div className="kpi-icon gate-icon">
              ▣
            </div>

            <div className="kpi-content">

              <div className="kpi-label">
                Gate Status
              </div>

              <div
                className={`gate-status-value ${gateStatusClass}`}
              >
                {gateStatus}
              </div>

              <div className="kpi-change">
                {confidence !== null
                  ? `${this.formatConfidence(
                      confidence
                    )} confidence`
                  : "Current gate state"}
              </div>

            </div>

          </div>


          {/* Current Duration */}

          <div className="gate-kpi-card">

            <div className="kpi-icon duration-icon">
              ◷
            </div>

            <div className="kpi-content">

              <div className="kpi-label">
                Current Duration
              </div>

              <div className="kpi-value">
                {isOpen
                  ? this.formatDuration(
                      currentOpenDuration
                    )
                  : "—"}
              </div>

              <div className="kpi-change">
                {isOpen
                  ? "Gate currently open"
                  : "Gate closed"}
              </div>

            </div>

          </div>


          {/* Last Plate */}

          <div className="gate-kpi-card">

            <div className="kpi-icon plate-kpi-icon">
              ▤
            </div>

            <div className="kpi-content">

              <div className="kpi-label">
                Last Detected Plate
              </div>

              <div className="kpi-plate">
                {lastPlate}
              </div>

              <div className="kpi-change">
                {plateDetectedAt
                  ? this.formatTime(
                      plateDetectedAt
                    )
                  : "No plate detected"}
              </div>

            </div>

          </div>


          {/* Plate Confidence */}

          <div className="gate-kpi-card">

            <div className="kpi-icon plate-confidence-icon">
              %
            </div>

            <div className="kpi-content">

              <div className="kpi-label">
                Plate Confidence
              </div>

              <div className="kpi-value">
                {this.formatConfidence(
                  plateConfidence
                )}
              </div>

              <div className="kpi-change">
                Number plate recognition
              </div>

            </div>

          </div>

        </div>


        {/* =====================================================
            MAIN GRID
        ====================================================== */}

        <div className="gate-main-grid">

          {/* =================================================
              GATE STATUS
          ================================================== */}

          <div className="gate-panel gate-status-panel">

            <div className="panel-header">

              <span>
                <span className="panel-icon">
                  ▣
                </span>

                Gate Status
              </span>

              <span
                className={`gate-state-badge ${gateBadgeClass}`}
              >
                {isOpen
                  ? "OPEN"
                  : isClosed
                    ? "CLOSED"
                    : "UNKNOWN"}
              </span>

            </div>


            <div className="gate-status-content">

              <div
                className={`gate-visual ${
                  isOpen
                    ? "gate-visual-open"
                    : isUnknown
                      ? "gate-visual-unknown"
                      : "gate-visual-closed"
                }`}
              >

                <div className="gate-left">
                  ║
                </div>

                <div className="gate-right">
                  ║
                </div>

              </div>


              <div
                className={`large-gate-status ${gateStatusClass}`}
              >
                {gateStatus}
              </div>


              <div className="gate-duration">

                {isOpen
                  ? `Open for ${this.formatDuration(
                      currentOpenDuration
                    )}`
                  : isUnknown
                    ? "Waiting for stable state"
                    : "Gate is currently closed"}

              </div>


              {isOpen &&
                openedAt && (
                  <div className="gate-open-time">

                    Opened at{" "}

                    {this.formatTime(
                      openedAt
                    )}

                  </div>
                )}


              <div className="gate-context">

                <span>
                  Gate
                </span>

                <span>
                  {gateName}
                </span>

                <span>
                  Site
                </span>

                <span>
                  {siteName}
                </span>

              </div>

            </div>

          </div>


          {/* =================================================
              NUMBER PLATE DETECTION
          ================================================== */}

          <div className="gate-panel plate-panel">

            <div className="panel-header">

              <span>

                <span className="panel-icon">
                  ▤
                </span>

                Number Plate Detection

              </span>


              <span
                className={`plate-status-badge ${
                  lastPlate !== "—"
                    ? "plate-detected"
                    : "plate-not-detected"
                }`}
              >

                {lastPlate !== "—"
                  ? "DETECTED"
                  : "WAITING"}

              </span>

            </div>


            <div className="plate-detection-content">

              <div className="plate-detection-label">
                Latest Recognized Number Plate
              </div>


              <div className="large-number-plate">
                {lastPlate}
              </div>


              <div className="plate-meta-grid">

                <div className="plate-meta">

                  <span>
                    Confidence
                  </span>

                  <strong>
                    {this.formatConfidence(
                      plateConfidence
                    )}
                  </strong>

                </div>


                <div className="plate-meta">

                  <span>
                    Detected At
                  </span>

                  <strong>
                    {plateDetectedAt
                      ? this.formatTime(
                          plateDetectedAt
                        )
                      : "—"}
                  </strong>

                </div>

              </div>


              <div className="plate-status-row">

                <span className="plate-status-dot" />

                <span>
                  Number plate detection active
                </span>

              </div>

            </div>

          </div>

        </div>


        {/* =====================================================
            RECENT EVENTS
        ====================================================== */}

        <div className="gate-panel recent-events-panel">

          <div className="recent-events-header">

            <div>

              <span className="panel-icon">
                ☷
              </span>

              Recent Gate Events

            </div>


            <button
              className="gate-view-btn"
              onClick={
                this.viewAllEvents
              }
            >
              View All Events →
            </button>

          </div>


          <div className="events-table-wrapper">

            <table className="events-table">

              <thead>

                <tr>

                  <th>
                    Time
                  </th>

                  <th>
                    Gate Event
                  </th>

                  <th>
                    Number Plate
                  </th>

                  <th>
                    Confidence
                  </th>

                  <th>
                    Status
                  </th>

                </tr>

              </thead>


              <tbody>

                {recentEvents.length ? (

                  recentEvents.map(
                    (event) => (

                      <tr
                        key={event.id}
                      >

                        <td>
                          {this.formatTime(
                            event.time
                          )}
                        </td>


                        <td>

                          <span
                            className={`event-name ${
                              event.event ===
                              "Gate Open"
                                ? "event-gate-open"
                                : event.event ===
                                    "Gate Close"
                                  ? "event-gate-close"
                                  : ""
                            }`}
                          >

                            <span className="event-arrow">

                              {event.event ===
                              "Gate Open"
                                ? "↗"
                                : event.event ===
                                    "Gate Close"
                                  ? "↙"
                                  : "•"}

                            </span>

                            {event.event}

                          </span>

                        </td>


                        <td>

                          <span className="vehicle-number">

                            {event.vehicleNumber ||
                              "—"}

                          </span>

                        </td>


                        <td>

                          {this.formatConfidence(
                            event.confidence
                          )}

                        </td>


                        <td>

                          <span className="normal-status">

                            {event.status ||
                              "NORMAL"}

                          </span>

                        </td>

                      </tr>

                    )
                  )

                ) : (

                  <tr>

                    <td
                      colSpan="5"
                      className="empty-events"
                    >
                      {loading
                        ? "Loading gate events..."
                        : "No gate events found."}
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </div>


        {/* =====================================================
            FOOTER
        ====================================================== */}

        <div className="gate-footer">

          <span
            className={`live-dot ${
              error
                ? "live-dot-error"
                : ""
            }`}
          />

          <span>
            {error
              ? "Offline"
              : DEMO_MODE
                ? "Demo"
                : "Live"}
          </span>

          <span className="gate-updated">

            {lastUpdated
              ? `Updated ${this.formatTime(
                  lastUpdated
                )}`
              : "Loading..."}

          </span>

        </div>

      </div>
    );
  }
}

export default GateDetection;